/** Copyright (c) 2017 T. Rowe Price. All rights reserved. */

import '../../../style/components/ModuleTable.scss';
import './ActivitySummaryTable.scss';

import { assign, map, concat, pick, values } from 'lodash';
import React, { Fragment } from 'react';
import { connect } from 'react-redux';

import { currency, yyyymmdd2mmmddyyyy } from 'services/Dates';
import WithUserPermissions from 'components/shared/WithUserPermissions/WithUserPermissions';

const Data = ({ val, suffix }) => (
  <Fragment>
    {val}
    {suffix}
  </Fragment>
);

const DataWithPermissions = WithUserPermissions(Data);

const COL_LABEL_NAME = '';
const COL_LABEL_ACCOUNT = 'Account';
const COL_LABEL_BEGINNING_VALUE = 'Beginning Balance';
const COL_LABEL_ADDITIONS = 'Additions';
const COL_LABEL_DEDUCTIONS = 'Deductions';
const COL_LABEL_INCOME = 'Income';
const COL_LABEL_MARKET_FLUCTUATION = 'Market Fluctuation';
const COL_LABEL_ENDING_VALUE = 'Ending Balance';

// TODO : deprecated - replaced by ActivitySummaryBreakdownGrid
class ActivitySummaryTable extends React.Component {
  constructor(props) {
    super(props);
    this.fluctuationClass = this.fluctuationClass.bind(this);
    this.renderColumn = this.renderColumn.bind(this);
    this.renderTotal = this.renderTotal.bind(this);
    this.renderData = this.renderData.bind(this);
    this.rowClickHandler = this.rowClickHandler.bind(this);
    this.viewAllRowsClick = this.viewAllRowsClick.bind(this);

    this.state = {
      viewAllRows: false
    };
  }

  fluctuationClass(val) {
    if (val == '$0.00') {
      return 'fluctuation-none';
    } else if (val.startsWith('$')) {
      return 'fluctuation-up';
    } else if (val.startsWith('-')) {
      return 'fluctuation-down';
    }
    return 'fluctuation-none';
  }

  renderColumn(col, colIndex) {
    return (
      <th key={colIndex + '-column'} className={this.columnClassName(col)}>
        {col}
      </th>
    );
  }

  renderTotal(val, colIndex) {
    return this.renderData(val, 0, colIndex, '-total');
  }

  renderData(val, accountIndex, colIndex, key) {
    const col = this.props.model.columns[colIndex];
    const suffix =
      col === COL_LABEL_MARKET_FLUCTUATION ? (
        <span className={this.fluctuationClass(val)} />
      ) : null;

    return (
      <td
        key={accountIndex + '-' + colIndex + key}
        className={this.columnClassName(col)}>
        <DataWithPermissions val={val} suffix={suffix} shouldMask />
      </td>
    );
  }

  rowClickHandler(event) {
    let el = event.target;

    while (el != null && el.nodeName.toLowerCase() !== 'tr') {
      el = el.parentNode;
    }

    if (el == null) {
      return;
    }

    el.classList.toggle('expanded');
  }

  viewAllRowsClick() {
    this.setState({ viewAllRows: true });
  }

  sortFunds(fundA, fundB) {
    var fundNameA = fundA[0].toUpperCase();
    var fundNameB = fundB[0].toUpperCase();
    if (fundNameA < fundNameB) {
      return -1;
    }
    if (fundNameA > fundNameB) {
      return 1;
    }

    return 0;
  }

  sortAccounts(accountA, accountB) {
    if (accountA !== accountB) {
      return accountA.totals[0].toUpperCase() < accountB.totals[0].toUpperCase()
        ? -1
        : 1;
    }

    return 0;
  }

  columnClassName(col) {
    if (col === '') {
      return 'heading';
    }
    return (col || '').toLocaleLowerCase().replace(/[^\w]/g, '-');
  }

  render() {
    const { maxInitialRowsToShow, model } = this.props;
    const { viewAllRows } = this.state;

    const rows = [];

    rows.push({
      className: 'totals',
      cells: model.totals.map((val, i) => this.renderTotal(val, i))
    });

    model.purposes.forEach((purpose, i) => {
      rows.push({
        className: 'purpose',
        cells: purpose.totals.map((val, j) =>
          this.renderData(val, i, j, '-purpose')
        )
      });

      purpose.accounts.forEach((account, i) => {
        rows.push({
          className: 'account',
          cells: account.totals.map((val, j) =>
            this.renderData(val, i, j, '-account')
          )
        });

        account.funds.forEach((fund, i) => {
          rows.push({
            className: 'fund',
            cells: fund.map((val, j) => this.renderData(val, i, j, '-fund'))
          });
        });
      });
    });

    if (!viewAllRows && rows.length > maxInitialRowsToShow) {
      for (let i = maxInitialRowsToShow - 1; i < rows.length; i++) {
        rows[i].className += ' later-row';
      }

      rows.splice(maxInitialRowsToShow - 1, 0, {
        className: 'view-more-link-row',
        noRowClick: true,
        cells: (
          <td className="view-more-link-cell" colSpan={model.totals.length}>
            <a className="view-more-link" onClick={this.viewAllRowsClick}>
              View complete Activity Summary breakdown.{' '}
              {rows.length - maxInitialRowsToShow + 1} more rows available.
            </a>
          </td>
        )
      });
    }

    return (
      <div
        id="activity-summary-table"
        className="module pure-u-1 module-container">
        <table className="summary-table">
          <thead>
            <tr className="columns">
              {model.columns.map((col, i) => this.renderColumn(col, i))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr
                className={row.className}
                key={row.className + i}
                onClick={!row.noRowClick ? this.rowClickHandler : null}>
                {row.cells}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }
}

const initialModel = {
  columns: [
    COL_LABEL_NAME,
    COL_LABEL_ACCOUNT,
    COL_LABEL_BEGINNING_VALUE,
    COL_LABEL_ADDITIONS,
    COL_LABEL_DEDUCTIONS,
    COL_LABEL_INCOME,
    COL_LABEL_MARKET_FLUCTUATION,
    COL_LABEL_ENDING_VALUE
  ]
};

const aggregateValues = (aggregate) => {
  return values(
    pick(
      aggregate,
      'beginningMarketValue',
      'additions',
      'deductions',
      'income',
      'marketFluctuation',
      'endingMarketValue'
    )
  );
};

const tableModel = (activitySummary) => {
  const totals = concat(
    ['Total Balance', ''],
    map(aggregateValues(activitySummary.aggregate), currency)
  );
  const purposes = map(activitySummary.purposes, (pval) => {
    const types = map(pval.types, (tval) => {
      const accounts = map(tval.accounts, (aval) => {
        return concat(
          [aval.accountDescriptor, aval.accountNumber],
          map(aggregateValues(aval.accountAggregate), currency)
        );
      });
      const type = {
        totals: concat(
          [tval.header, ''],
          map(aggregateValues(tval.aggregate), currency)
        ),
        funds: accounts
      };
      return type;
    });
    const purpose = {
      totals: concat(
        [pval.header, ''],
        map(aggregateValues(pval.aggregate), currency)
      ),
      accounts: types
    };
    return purpose;
  });

  return assign({}, initialModel, { totals: totals, purposes: purposes });
};

ActivitySummaryTable.propTypes = {};

const mapStateToProps = ({ view, activitySummaryTable }) => ({
  fromDate: yyyymmdd2mmmddyyyy(view ? view.beginDate : ''),
  toDate: yyyymmdd2mmmddyyyy(view ? view.endDate : ''),
  model: tableModel(activitySummaryTable),
  maxInitialRowsToShow: view.maxInitialRowsToShow
});

export default connect(mapStateToProps)(ActivitySummaryTable);
